import { ColumnSizingState, SortingState } from '@tanstack/react-table'
import agents from '../../../api/agents/hooks'
import auth from '../../../api/auth/hooks'

// Add module-specific overrides here; all others fall back to 'default'
export const DEFAULT_COLUMN_SORT: Record<string, SortingState> = {
    opportunityActivityTable: [{ id: 'dateFrom', desc: true }],
    opportunityContactTable: [{ id: 'contactPersonName', desc: false }],
    intermediaryDiary: [{ id: 'internalAppPropertyId', desc: true }],
    evidenceDiary: [{ id: 'internalAppPropertyId', desc: true }],
    inDepthAnalysis: [{ id: 'id', desc: true }],
    inDepthAnalysisContactsConnectedToCompany: [{ id: 'id', desc: true }],
    default: [{ id: 'internalAppId', desc: true }],
}

const useTableSettings = (tableKey: string) => {
    const { mutate: updateAgentSettings } = agents.UpdateCurrentAgentSettings.useMutation()
    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({ id: data.id, userSettings: data.userSettings }),
    })

    const currentAgentId = currentAgent?.id
    const userSettings = currentAgent?.userSettings

    const sortKey = `${tableKey}_columnSort`
    const hiddenColumnsKey = `${tableKey}_hiddenColumns`
    const columnOrderKey = `${tableKey}_columnOrder`
    const columnSizingKey = `${tableKey}_columnSizing`
    const pageSizeKey = `${tableKey}_pageSize`

    const storedSort = (userSettings?.[sortKey] as SortingState | undefined) ?? null
    const storedHiddenColumns = (userSettings?.[hiddenColumnsKey] as string[] | undefined) ?? null
    const storedColumnOrder = (userSettings?.[columnOrderKey] as string[] | undefined) ?? null
    const storedColumnSizing = (userSettings?.[columnSizingKey] as ColumnSizingState | undefined) ?? null
    const storedPageSize = (userSettings?.[pageSizeKey] as number | undefined) ?? null

    const save = (changes: Record<string, unknown>) => {
        if (!currentAgentId) return

        updateAgentSettings({
            agentId: currentAgentId,
            userSettings: { ...userSettings, ...changes },
        })
    }

    const updateSort = (sort: SortingState) => save({ [sortKey]: sort })
    const updateVisibility = (hidden: string[]) => save({ [hiddenColumnsKey]: hidden })
    const updateColumnOrder = (order: string[]) => save({ [columnOrderKey]: order })
    const updateColumnSizing = (sizing: ColumnSizingState) => save({ [columnSizingKey]: sizing })
    const updatePageSize = (size: number) => save({ [pageSizeKey]: size })

    return {
        storedSort,
        updateSort,
        storedHiddenColumns,
        updateVisibility,
        storedColumnOrder,
        updateColumnOrder,
        storedColumnSizing,
        updateColumnSizing,
        storedPageSize,
        updatePageSize,
    }
}

export default useTableSettings
